import React from 'react';
// import { Order } from '../../types';
import { getDateUrgency, getDateColorClass } from '../../utils/dateUtils';
import { BsExclamationCircle } from 'react-icons/bs';
import { OutboundOrder } from '../../../../api/warehouse/generated/src';

interface OrdersViewProps {
  order: OutboundOrder;
}

export const OrdersView: React.FC<OrdersViewProps> = ({ order }) => {
  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-4">Auftragsübersicht</h2>
      
      {order.subOrders && order.subOrders.length > 0 ? (
        <>
          <div className="mb-4">
            <h3 className="font-semibold text-base mb-2">Unteraufträge</h3>
            <div className="space-y-3">
              {order.subOrders.map((subOrder, idx) => {
                const urgency = getDateUrgency(subOrder.deliveryDate);
                const urgencyClass = getDateColorClass(urgency);
                
                return (
                  <div key={idx} className={`rounded-lg border overflow-hidden ${
                    urgency === 'overdue' ? 'border-red-300' :
                    urgency === 'urgent' ? 'border-orange-300' :
                    'border-gray-300'
                  }`}>
                    <div className={`p-3 ${
                      urgency === 'overdue' ? 'bg-red-50' :
                      urgency === 'urgent' ? 'bg-orange-50' :
                      'bg-gray-50'
                    }`}>
                      <div className="flex justify-between items-center">
                        <span className="font-semibold">{subOrder.orderNumber}</span>
                        <div className="flex items-center gap-2">
                          {urgency === 'overdue' && (
                            <span className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded-full">
                              ÜBERFÄLLIG
                            </span>
                          )}
                          {urgency === 'urgent' && (
                            <span className="px-2 py-1 bg-orange-600 text-white text-xs font-bold rounded-full">
                              DRINGEND
                            </span>
                          )}
                          <span className={`text-sm font-semibold ${urgencyClass}`}>
                            {new Date(subOrder.deliveryDate).toLocaleDateString('de-DE')}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-white">
                      <p className="text-sm text-gray-600 mb-2">Zugeordnete Positionen:</p>
                      <div className="space-y-1">
                        {order.items.map((item, itemIdx) => {
                          const remainingQty = item.requiredQty - item.shippedQty;
                          const isFirstSubOrder = idx === 0;
                          const assignedQty = isFirstSubOrder ? 
                            Math.min(item.shippedQty + item.pickedQty, item.requiredQty) : 
                            Math.max(0, remainingQty - item.pickedQty);
                          
                          if (assignedQty === 0) return null;
                          
                          return (
                            <div key={itemIdx} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                              <div>
                                <p className="font-semibold text-sm">{item.articleNumber}</p>
                                <p className="text-xs text-gray-600">{item.description}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-semibold">{assignedQty} Stk</p>
                                {isFirstSubOrder && item.shippedQty > 0 && (
                                  <p className="text-xs text-green-600">({item.shippedQty} geliefert)</p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          <div className="p-3 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-sm mb-2">Gesamtübersicht</h4>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="bg-white rounded p-2 text-center">
                <span className="text-gray-600">Gesamt: </span>
                <span className="font-bold">{order.items.reduce((sum, item) => sum + item.requiredQty, 0)} Stk</span>
              </div>
              <div className="bg-white rounded p-2 text-center">
                <span className="text-gray-600">Geliefert: </span>
                <span className="font-bold text-green-600">{order.items.reduce((sum, item) => sum + item.shippedQty, 0)} Stk</span>
              </div>
              <div className="bg-white rounded p-2 text-center">
                <span className="text-gray-600">Offen: </span>
                <span className="font-bold text-orange-600">{order.items.reduce((sum, item) => sum + (item.requiredQty - item.shippedQty), 0)} Stk</span>
              </div>
            </div>
            
            {order.hasPartialDelivery && (
              <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-200">
                <div className="flex items-center gap-2">
                  <BsExclamationCircle size={16} className="text-amber-600" />
                  <p className="text-xs text-amber-800 font-semibold">
                    Teillieferung erfolgt - Kunde wartet auf Restlieferung
                  </p>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="mb-4">
            <h3 className="font-semibold text-base mb-2">Auftrag</h3>
            <div className={`p-3 rounded-lg border ${
              getDateUrgency(order.deliveryDate) === 'overdue' ? 'bg-red-50 border-red-300' :
              getDateUrgency(order.deliveryDate) === 'urgent' ? 'bg-orange-50 border-orange-300' :
              'bg-gray-50 border-gray-300'
            }`}>
              <div className="flex justify-between items-center">
                <span className="font-semibold">{order.orderNumber}</span>
                <div className="flex items-center gap-2">
                  {getDateUrgency(order.deliveryDate) === 'overdue' && (
                    <span className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded-full">
                      ÜBERFÄLLIG
                    </span>
                  )}
                  {getDateUrgency(order.deliveryDate) === 'urgent' && (
                    <span className="px-2 py-1 bg-orange-600 text-white text-xs font-bold rounded-full">
                      DRINGEND
                    </span>
                  )}
                  <span className={`text-sm font-semibold ${getDateColorClass(getDateUrgency(order.deliveryDate))}`}>
                    {new Date(order.deliveryDate).toLocaleDateString('de-DE')}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="p-4 bg-blue-50 rounded-lg">
            <h3 className="font-semibold text-base mb-3">Gesamtübersicht</h3>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-white rounded-lg p-3 text-center">
                <p className="text-xs text-gray-600 mb-1">Gesamt</p>
                <p className="text-xl font-bold">{order.items.reduce((sum, item) => sum + item.requiredQty, 0)}</p>
                <p className="text-xs text-gray-500">Stück</p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <p className="text-xs text-gray-600 mb-1">Geliefert</p>
                <p className="text-xl font-bold text-green-600">{order.items.reduce((sum, item) => sum + item.shippedQty, 0)}</p>
                <p className="text-xs text-gray-500">Stück</p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <p className="text-xs text-gray-600 mb-1">Offen</p>
                <p className="text-xl font-bold text-orange-600">{order.items.reduce((sum, item) => sum + (item.requiredQty - item.shippedQty), 0)}</p>
                <p className="text-xs text-gray-500">Stück</p>
              </div>
            </div>
            
            <h4 className="font-semibold text-sm text-gray-700 mb-2">Positionen im Detail</h4>
            <div className="space-y-2">
              {order.items.map((item, idx) => {
                const remainingQty = item.requiredQty - item.shippedQty;
                return (
                  <div key={idx} className="bg-white rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-semibold text-sm">{item.articleNumber}</p>
                        <p className="text-sm text-gray-600">{item.description}</p>
                      </div>
                      <div className="text-right">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                          <span className="text-gray-600">Gesamt:</span>
                          <span className="font-semibold">{item.requiredQty} Stk</span>
                          
                          {item.shippedQty > 0 && (
                            <>
                              <span className="text-gray-600">Geliefert:</span>
                              <span className="font-semibold text-green-600">{item.shippedQty} Stk</span>
                            </>
                          )}
                          
                          {remainingQty > 0 && (
                            <>
                              <span className="text-gray-600">Offen:</span>
                              <span className="font-semibold text-orange-600">{remainingQty} Stk</span>
                            </>
                          )}
                          
                          {item.pickedQty > 0 && (
                            <>
                              <span className="text-gray-600">Gepickt:</span>
                              <span className="font-semibold text-blue-600">{item.pickedQty} Stk</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {order.hasPartialDelivery && (
              <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2">
                  <BsExclamationCircle size={18} className="text-amber-600" />
                  <p className="text-sm text-amber-800 font-semibold">
                    Teillieferung erfolgt - Kunde wartet auf Restlieferung
                  </p>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// export { OrdersView };